import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GamePreferencesProvider } from "./contexts/GamePreferencesContext";
import { SiteConfigProvider } from "./contexts/SiteConfigContext";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";
import { FoundingBanner } from "./components/FoundingBanner";
import { HelpReplyBanner } from "./components/HelpReplyBanner";
import { IS_ANDROID_APP } from "./utils/platform";
import { useEffect } from "react";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import PlayPage from "./pages/PlayPage";
import AdvancedLabPage from "./pages/AdvancedLabPage";
import LessonsPage from "./pages/LessonsPage";
import LessonDetailPage from "./pages/LessonDetailPage";
import ValidatorPage from "./pages/ValidatorPage";
import GamePage from "./pages/GamePage";
import TutorialsPage from "./pages/TutorialsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import DailyChallengePage from "./pages/DailyChallengePage";
import ReferralPage from "./pages/ReferralPage";
import AchievementsPage from "./pages/AchievementsPage";
import SciencePage from "./pages/SciencePage";
import AdminPage from "./pages/AdminPage";
import SolverPage from "./pages/SolverPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import ScanPage from "./pages/ScanPage";
import PrivacyPage from "./pages/PrivacyPage";
import SupportPage from "./pages/SupportPage";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-main)' }}>
        <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-main)' }}>
        <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Semi-protected (can be used without auth but better with) */}
      <Route path="/play" element={<PlayPage />} />
      <Route path="/advanced-lab" element={<AdvancedLabPage />} />
      <Route path="/lessons" element={<LessonsPage />} />
      <Route path="/lessons/:code" element={<LessonDetailPage />} />
      <Route path="/validator" element={<ValidatorPage />} />
      <Route path="/scan" element={<ScanPage />} />
      <Route path="/play/:puzzleId" element={<GamePage />} />
      <Route path="/daily" element={<DailyChallengePage />} />
      <Route path="/classic-sudoku" element={<DailyChallengePage />} />
      <Route path="/daily/:puzzleId" element={<GamePage />} />
      <Route path="/tutorials" element={<TutorialsPage />} />
      <Route path="/tutorials/:techniqueId" element={<TutorialsPage />} />
      <Route path="/solver" element={<SolverPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/subscription" element={IS_ANDROID_APP ? <Navigate to="/" replace /> : <SubscriptionPage />} />
      <Route path="/subscription/success" element={IS_ANDROID_APP ? <Navigate to="/" replace /> : <SubscriptionPage />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/achievements" element={<AchievementsPage />} />
      <Route path="/science" element={<SciencePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/support" element={<SupportPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // When a brand-new service worker takes control (i.e. an old cache
    // was just purged), reload exactly once so the page is running on
    // the latest hashed JS bundle. Without this, users on iOS Safari
    // could be pinned to a stale `index.html` that referenced bundles
    // the server no longer ships, breaking premium gating, stats, etc.
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });

    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        // If a waiting SW is already sitting there, prod it to activate.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              // New SW installed alongside an existing controller —
              // tell it to skipWaiting so controllerchange fires.
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {});
  }, []);

  return (
    <div className="App">
      <ThemeProvider>
        <GamePreferencesProvider>
        <BrowserRouter>
          <AuthProvider>
            <SiteConfigProvider>
              <ScrollToTop />
              <FoundingBanner />
              <HelpReplyBanner />
              <AppRoutes />
              <Toaster position="top-center" richColors />
              <InstallPrompt />
            </SiteConfigProvider>
          </AuthProvider>
        </BrowserRouter>
        </GamePreferencesProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
